import { queryOptions, type EnsureQueryDataOptions, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { notFound, redirect } from "@tanstack/react-router";
import {
  RpcError,
  RpcRedirect,
  type MemberViewKey,
  type OptionalArg,
  type PublicViewKey,
  type QueryOf,
  type ShareKind,
  type ShareViewKey,
  type ViewParams,
  type ViewResult,
} from "@buildpy/api/client";

import { LOGIN_PATH, loginHref } from "@buildpy/core/modules/onboarding/rules";

import { dropRejectedSession, isNoMemberError, publicView, shareView, view } from "@/api/client";
import { pendingHref } from "@/lib/nav/router-ref";
import { noteViewFetch, VIEW_KEY } from "@/lib/query";
import { hereHref, returnPathOf } from "@/lib/redirect-error";
import { ONBOARDING_PATH } from "@/modules/plan/copy";

// 멤버 읽기 — 키 머리는 VIEW_KEY 라 액션 뒤 무효화(D5)가 이 쿼리를 다시 읽는다.
export const viewQuery = <K extends MemberViewKey>(name: K, ...params: OptionalArg<ViewParams<K>>) =>
  queryOptions({
    queryKey: [VIEW_KEY, name, params[0] ?? {}] as const,
    queryFn: ({ queryKey }) => {
      noteViewFetch(queryKey);
      return view(name, ...params) as Promise<ViewResult<K>>;
    },
  });

export const shareViewQuery = <S extends ShareKind, K extends ShareViewKey<S>>(kind: S, token: string, name: K, ...params: OptionalArg<QueryOf<K, "token">>) =>
  queryOptions({
    queryKey: [VIEW_KEY, `${kind}:${name}`, token, params[0] ?? {}] as const,
    queryFn: ({ queryKey }) => {
      noteViewFetch(queryKey);
      return shareView(kind, token, name, ...params) as Promise<ViewResult<K>>;
    },
  });

export const publicViewQuery = <K extends PublicViewKey>(name: K, path: readonly string[] = [], ...params: OptionalArg<QueryOf<K, "path">>) =>
  queryOptions({
    queryKey: [VIEW_KEY, `p:${name}`, path, params[0] ?? {}] as const,
    queryFn: ({ queryKey }) => {
      noteViewFetch(queryKey);
      return publicView(name, path, ...params) as Promise<ViewResult<K>>;
    },
  });

export { LOGIN_PATH };

// next 를 안 넘긴 401 의 돌아올 주소 — loader 가 불러오던 라우터 주소, 라우터 전이면 지금 화면 주소.
const defaultNext = (): string | undefined => {
  const target = pendingHref();
  return target === undefined ? hereHref() : returnPathOf(target);
};

// 읽기 실패를 라우터 신호로 바꾼다 — 이동은 redirect · 404 는 notFound · 401 no_member 는 /onboarding · 그 밖 401 은 로그인으로(?next= 는 받은 것, 없으면 defaultNext).
export const toRouterSignal = (error: unknown, next?: string): unknown => {
  if (error instanceof RpcRedirect) return redirect({ href: error.to });
  if (error instanceof RpcError && error.status === 404) return notFound();
  if (isNoMemberError(error)) return redirect({ href: ONBOARDING_PATH });
  if (error instanceof RpcError && error.status === 401) return redirect({ href: loginHref(next ?? defaultNext()) });
  return error;
};

// 로그인 없는 읽기(고객 링크 s:·c: · 공개 p:) — 401 을 로그인 신호로 바꾸지 않고 화면의 오류 경계로 보낸다(멤버 세션도 지우지 않는다).
export const isOpenViewKey = (key: QueryKey): boolean => typeof key[1] === "string" && /^[scp]:/.test(key[1]);

// 규칙 — 화면은 useSuspenseQuery(viewQuery(...)) 로만 읽고 loader 는 이것으로 캐시만 채운다(useLoaderData 는 무효화 뒤 옛 값이라 쓰지 않는다).
export const loadView = async <TQueryFnData, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
  client: QueryClient,
  options: EnsureQueryDataOptions<TQueryFnData, Error, TData, TQueryKey>,
  next?: string,
): Promise<void> => {
  try {
    await client.ensureQueryData(options);
  } catch (error) {
    const unauthorized = error instanceof RpcError && error.status === 401;
    if (unauthorized && isOpenViewKey(options.queryKey)) throw error;
    if (unauthorized) await dropRejectedSession(error);
    throw toRouterSignal(error, next);
  }
};
